import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatApiError } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";

const keyOf = (i) => `${i.sole_code}||${i.sole_color || ""}||${i.size}`;

// Edit/delete a sole receipt; with `order`, every order line is editable and capped by what is still open
export default function SoleReceiptEditDialog({ receipt, order, onOpenChange, onSaved }) {
  const [qty, setQty] = useState({});
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!receipt) return;
    const q = {};
    receipt.items.forEach((i) => { q[keyOf(i)] = (q[keyOf(i)] || 0) + i.qty; });
    setQty(q); setNote(receipt.note || "");
  }, [receipt]);

  if (!receipt) return null;
  const rows = (order ? order.items : receipt.items).map((i) => {
    const k = keyOf(i), mine = receipt.items.filter((x) => keyOf(x) === k).reduce((a, b) => a + b.qty, 0);
    return { ...i, k, max: order ? i.qty - (i.received_qty || 0) + mine : null };
  }).filter((r, idx, arr) => arr.findIndex((x) => x.k === r.k) === idx);

  const send = async (items) => {
    setSaving(true);
    try {
      if (items.length) await api.put(`/soles/receipts/${receipt.id}`, { note, items });
      else await api.delete(`/soles/receipts/${receipt.id}`);
      toast.success(items.length ? "Taban girişi güncellendi, stok düzeltildi" : "Taban girişi silindi, stoktan düşüldü");
      onOpenChange(false); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const save = () => {
    for (const r of rows) {
      const q = parseInt(qty[r.k]) || 0;
      if (q < 0) { toast.error("Adet eksi olamaz"); return; }
      if (r.max !== null && q > r.max) { toast.error(`${r.sole_code} N°${r.size}: en fazla ${r.max} çift`); return; }
    }
    const items = rows.filter((r) => parseInt(qty[r.k]) > 0).map((r) => ({ sole_code: r.sole_code, sole_color: r.sole_color || "", size: r.size, qty: parseInt(qty[r.k]) }));
    if (!items.length && !window.confirm("Tüm adetler 0. Bu taban girişi silinsin mi? Gelen adetler stoktan düşülür.")) return;
    send(items);
  };

  return (
    <Dialog open={!!receipt} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#FFFBF4] border-[#DCCFB8] text-[#2B2118] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="sole-receipt-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl uppercase">Taban Girişini Düzenle</DialogTitle>
          <p className="text-xs text-[#6B5B4A]">{receipt.supplier || "Tedarikçi"} · {new Date(receipt.created_at).toLocaleString("tr-TR")}. Değişiklik kadar taban stoğu{order ? " ve sipariş teslim miktarı" : ""} düzeltilir. 0 yazılan numara girişten çıkarılır.</p>
        </DialogHeader>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {rows.map((r) => (
            <label key={r.k} className="rounded-md border border-[#DCCFB8] bg-[#EFE6D6] p-2 text-[11px] font-mono text-[#6B5B4A]">
              {r.sole_code}{r.sole_color ? `·${r.sole_color}` : ""} · N°{r.size}
              <Input type="number" min={0} max={r.max ?? undefined} value={qty[r.k] ?? ""} placeholder="0"
                onChange={(e) => setQty({ ...qty, [r.k]: e.target.value })} data-testid={`sole-receipt-edit-qty-${r.sole_code}-${r.sole_color || "none"}-${r.size}`}
                className="mt-1 h-9 text-center font-mono font-bold bg-[#FFFBF4] border-[#DCCFB8]" />
              {r.max !== null && <span className="block mt-0.5 text-[10px]">en fazla {r.max}</span>}
            </label>
          ))}
        </div>
        <Input placeholder="Not" value={note} onChange={(e) => setNote(e.target.value)} data-testid="sole-receipt-edit-note" className="h-10 bg-[#EFE6D6] border-[#DCCFB8]" />
        <DialogFooter className="flex justify-between sm:justify-between">
          <Button variant="outline" disabled={saving} data-testid="sole-receipt-delete"
            onClick={() => window.confirm("Bu taban girişi silinsin mi? Gelen adetler stoktan düşülür.") && send([])}
            className="border-[#EF4444]/50 bg-transparent text-[#B91C1C] hover:bg-[#FDECEC]"><Trash2 className="w-4 h-4 mr-1" /> Girişi Sil</Button>
          <Button onClick={save} disabled={saving} data-testid="sole-receipt-save" className="bg-[#D97706] hover:bg-[#B45309] text-white">
            {saving ? "Kaydediliyor..." : "Kaydet"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
